import type { AuthSignInWithGoogleData } from "@oboku/shared"
import { from, switchMap } from "rxjs"
import { useReCreateDb } from "../rxdb"
import { useHttpClientApi } from "../http"
import { useMutation$ } from "reactjrx"
import { signInWithGooglePrompt } from "../integrations/google/auth"
import { useConfig } from "../config/useConfig"
import { completeAuthentication } from "./completeAuthentication"
import { useForgetAccountSessions } from "./useForgetAccountSessions"
import { usePutProfile } from "../profiles"
import { getOrCreateInstallationId } from "../common/browser"
import { createProofKey } from "./proofKey"
import { withLock } from "../common/locks/utils"
import {
  type DefaultError,
  type UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query"

type SignInVariables = { email: string; password: string } | undefined
type SignInData = { switchedAccount: boolean }

export const useSignIn = (
  options?: Pick<
    UseMutationOptions<SignInData, DefaultError, SignInVariables>,
    "onSuccess" | "onError" | "onSettled"
  >,
) => {
  const httpClientApi = useHttpClientApi()
  const { mutateAsync: reCreateDb } = useReCreateDb()
  const { mutateAsync: putProfile } = usePutProfile()
  const forgetAccountSessions = useForgetAccountSessions()
  const queryClient = useQueryClient()
  const { data: config } = useConfig()

  return useMutation$<SignInData, DefaultError, SignInVariables>({
    ...options,
    mutationFn: (data) => {
      const installationId = getOrCreateInstallationId()

      return from(createProofKey()).pipe(
        switchMap((proofKey) =>
          (data
            ? from(
                httpClientApi.signInWithEmail(
                  {
                    ...data,
                    installation_id: installationId,
                    public_key: proofKey.publicJwk,
                  },
                  proofKey,
                ),
              )
            : signInWithGooglePrompt(config?.GOOGLE_CLIENT_ID ?? "").pipe(
                switchMap((authResponse) =>
                  from(
                    httpClientApi.signInWithGoogle(
                      {
                        token: authResponse.credential,
                        installation_id: installationId,
                        public_key: proofKey.publicJwk,
                      } satisfies AuthSignInWithGoogleData["body"],
                      proofKey,
                    ),
                  ),
                ),
              )
          ).pipe(
            switchMap(({ data: auth }) =>
              completeAuthentication({
                reCreateDb,
                putProfile,
                forgetAccountSessions,
                auth,
                queryClient,
              }),
            ),
          ),
        ),
        withLock("authentication"),
      )
    },
  })
}
